import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { AdminSector } from '@/types/admin';
import { useToast } from '@/contexts/ToastContext';
import UserQuotaSection from '@/components/admin/UserQuotaSection';
import AssistantQuotaSection from '@/components/admin/AssistantQuotaSection';
import AgentFeatureToggleSection from '@/components/admin/AgentFeatureToggleSection';
import SeopFeatureToggleSection from '@/components/admin/SeopFeatureToggleSection';
import DashboardFeatureToggleSection from '@/components/admin/DashboardFeatureToggleSection';

const ROLES = [
  { value: 'LEARNER', label: 'Aprendiz' },
  { value: 'CURATOR', label: 'Curador' },
  { value: 'ADMIN', label: 'Administrador' },
];

const emptyForm = {
  email: '',
  name: '',
  role: 'LEARNER',
  squad: '' as string,
  sector_id: '' as string,
  password: '',
};

const AdminUserFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditing = !!id;

  const [form, setForm] = useState(emptyForm);
  const [sectors, setSectors] = useState<AdminSector[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    adminService.getSectors().then((data) => setSectors(data)).catch(() => {});
    if (isEditing) loadUser();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUser = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const user = await adminService.getUser(id);
      setForm({
        email: user.email,
        name: user.name,
        role: user.role,
        squad: user.squad || '',
        sector_id: user.sector?.id || '',
        password: '',
      });
    } catch {
      showToast('Erro ao carregar usuário', 'error');
      navigate('/admin/usuarios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.email.trim() || !form.name.trim()) {
      showToast('Email e nome são obrigatórios', 'warning');
      return;
    }
    if (!isEditing && !form.password) {
      showToast('Senha é obrigatória ao criar usuário', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        email: form.email,
        name: form.name,
        role: form.role,
        sector_id: form.sector_id || null,
      };
      if (form.squad) payload.squad = form.squad;
      else payload.squad = null;
      if (form.password) payload.password = form.password;

      if (isEditing) {
        await adminService.updateUser(id!, payload);
      } else {
        await adminService.createUser(payload as { email: string; name: string; role: string; password: string });
      }
      navigate('/admin/usuarios');
    } catch (err) {
      const error = err as { response?: { data?: Record<string, unknown> } };
      const data = error?.response?.data;
      if (data && typeof data === 'object') {
        const messages = Object.entries(data)
          .map(([key, val]) => {
            const msg = Array.isArray(val) ? val.join(', ') : String(val);
            return `${key}: ${msg}`;
          })
          .join('\n');
        showToast(messages || 'Erro ao salvar', 'error');
      } else {
        showToast('Erro ao salvar usuário', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass =
    'w-full bg-surface-card border border-theme-border rounded-lg px-4 py-2.5 text-content-primary placeholder-content-muted text-sm';

  if (isLoading) {
    return (
      <div className="p-6 lg:p-10">
        <p className="text-content-muted">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-2xl">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/usuarios')}
          className="p-2 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary transition-colors"
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
        </h1>
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1.5">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputClass}
            placeholder="usuario@empresa.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1.5">Nome</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputClass}
            placeholder="Nome completo"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1.5">Tipo de Usuário</label>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className={inputClass}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1.5">Setor</label>
          <select
            value={form.sector_id}
            onChange={(e) => setForm({ ...form, sector_id: e.target.value })}
            className={inputClass}
          >
            <option value="">Sem setor</option>
            {sectors.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1.5">
            Senha {isEditing && <span className="text-xs text-content-muted">(deixe vazio para manter)</span>}
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={inputClass}
            placeholder={isEditing ? 'Deixe vazio para manter a senha atual' : 'Mínimo 8 caracteres'}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
          <button
            onClick={() => navigate('/admin/usuarios')}
            className="bg-surface-card border border-theme-border text-content-primary px-6 py-2.5 rounded-xl font-medium hover:bg-surface-hover transition-colors"
          >
            Cancelar
          </button>
        </div>

        {/* Gate Hub de Agentes — só em modo edit (precisa de id). Vem antes de
            quota porque é a porta de entrada: sem acesso, quota é irrelevante. */}
        {isEditing && id && <AgentFeatureToggleSection userId={id} />}
        {/* Gate do Balanceamento de Linha — o módulo não entra na Sidebar, então
            liberar alguém só é possível por aqui. */}
        {isEditing && id && <SeopFeatureToggleSection userId={id} />}
        {isEditing && id && <DashboardFeatureToggleSection userId={id} />}
        {/* P13: cotas de IA — só em modo edit (precisa de id) */}
        {isEditing && id && <UserQuotaSection userId={id} />}
        {/* G10: cota do Assistente Geral (H.Bot) — tabela própria, endpoint separado */}
        {isEditing && id && <AssistantQuotaSection userId={id} />}
      </div>
    </div>
  );
};

export default AdminUserFormPage;
